/**
 * EditorialDatasheet — leader-dotted key/value rows grouped under hairline
 * category headers. The "kills the boxy collapsibles" treatment William
 * flagged: dense, scannable, print-clean, no cards.
 *
 * Editorial reference: `.dsgroup` / `.dsrow` in
 *   docs/design/round3-conclusion-extended-refs.html (Extended 21A):
 *     group header: mono small-caps, border-bottom 1.5px solid var(--ink),
 *       optional right-aligned <span> meta;
 *     row: key (var(--body)) ←dotted leader→ value (mono var(--ink)),
 *       border-bottom 1px dotted var(--hair-strong).
 *
 * Used by:
 *   - Extended Site Data (21A)   — Seismic / Renewable / Pricing / Env / Boundary
 *   - LCOE (D3 sparkline rows — see EditorialStackBar for the inline bars;
 *     this primitive owns the pure grouped datasheet)
 *
 * Each group's rows whose `value` is null/undefined/'' are DROPPED (no
 * fabricated specs); an empty group is omitted; an all-empty datasheet
 * returns null. A row may set `tone` to retint its value (e.g. a wetlands
 * flag in 'negative').
 *
 * Theme tokens read: 'ink' (group rule + values), 'muted' (group meta),
 * 'body' (keys), 'rule'/'hair' (dotted leaders), and status roles for a
 * toned value. Mono group headers + values via --report-body-font.
 * Pure / print-safe.
 *
 * ROWS ARE FLEX, NOT GRID (Redhead compactness QA, 2026-07-02) — the paged
 * deck stylesheet de-fangs any `[style*="grid"]` element inside a flow section
 * to `display:block !important` (PaginatedReportView ~L888, the pagedjs
 * grid-collapse workaround). With the row grid blocked, the key/value spans
 * fell back to inline flow and RAN TOGETHER with no separator ("Design
 * CategoryB", "AQI52") on BOTH the view tab and the PDF. Flex rows survive the
 * deck (the codebase-blessed pagedjs-safe layout — see the deck comment about
 * EditorialRecommendations), restoring the key ←gap→ right-aligned-value
 * ledger. Rows also now WRAP TWO-UP at the A4 measure (a `flex-basis` floor
 * per row) so a long datasheet reads as a compact two-column spec sheet
 * instead of one sparse full-width line per key — halving the section's page
 * cost. Narrow measures (a 2-col preset half-column) fall back to one row per
 * line. Do not reintroduce the substring "grid" into the inline style — the
 * deck selector matches on it.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor, type ReportColorRole } from '../../../../lib/reportRoleColors';
import { reportShellBodyFontSize, REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';
import { chunkPairs } from '../../../../lib/reportRendering/layoutGroups';

const MONO = 'var(--report-body-font, inherit)';

export type DatasheetTone = 'default' | 'positive' | 'warning' | 'negative';

export interface DatasheetRow {
  key: React.ReactNode;
  /** `null`/`undefined`/'' drops the row (no fabricated spec). */
  value: React.ReactNode;
  tone?: DatasheetTone;
}

export interface DatasheetGroup {
  /** Group header (mono small-caps). Optional for headless groups. */
  heading?: React.ReactNode;
  /** Optional right-aligned meta on the header rule (e.g. "SDC A · Risk Cat II"). */
  meta?: React.ReactNode;
  rows: DatasheetRow[];
}

export interface EditorialDatasheetProps {
  theme: ReportTheme;
  groups: DatasheetGroup[];
  /**
   * Pair compact values across the page or give every row the full measure.
   * Use `single` for paragraph-scale values: a two-up flex row is atomic in
   * Paged.js, so two long values can become taller than the remaining page and
   * be clipped instead of moving cleanly to the next sheet.
   */
  rowLayout?: 'paired' | 'single';
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

const TONE_ROLE: Record<DatasheetTone, ReportColorRole> = {
  default: 'ink',
  positive: 'positive',
  warning: 'warning',
  negative: 'negative',
};

function isRenderableValue(value: React.ReactNode): boolean {
  if (value == null || value === false) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

export function EditorialDatasheet({
  theme,
  groups,
  rowLayout = 'paired',
  id,
  className,
  style,
}: EditorialDatasheetProps): React.ReactElement | null {
  const dense = theme.density === 'compact';
  const ink = roleColor(theme, 'ink');
  const muted = roleColor(theme, 'muted');
  const body = roleColor(theme, 'body');
  const rule = roleColor(theme, 'rule');

  const renderGroups = groups
    .map((g) => ({ ...g, rows: g.rows.filter((r) => isRenderableValue(r.value)) }))
    .filter((g) => g.rows.length > 0);

  if (renderGroups.length === 0) return null;

  const renderCell = (row: DatasheetRow, key: number): React.ReactElement => (
    <div
      key={key}
      style={{
        flex: '1 1 15rem',
        minWidth: 0,
        display: 'flex',
        columnGap: 12,
        alignItems: 'baseline',
        padding: dense ? '4px 0' : '6px 0',
        borderBottom: `1px dotted ${rule}`,
        // 14px unified report BODY size (text-sm).
        fontSize: reportShellBodyFontSize(theme),
      }}
    >
      {/* BUG-9B (kept under flex) — the key must never be starved to
           ~1 char wide by a long value. `flex: 0 1 auto` + an 8rem
           `minWidth` is the exact flex twin of the former
           `minmax(8rem, max-content)` grid track: basis at max-content,
           free to shrink, but never past the 8rem floor.

           It was `flex: 0 0 auto` (shrink FORBIDDEN), which protected the
           key but made a long KEY unable to give way — max-content then
           pushed the row past its container. The financial overhaul's
           "Hurdle rate – target return (override)" overflowed the narrow
           editorial 2-col viewer column by 22px, failing
           `visual-matrix-editorial-gate` (print PDF stayed clean). The
           floor keeps BUG-9B's guarantee; allowing shrink lets a long key
           wrap at a space instead of overflowing. */}
      <span
        data-datasheet-key="true"
        style={{ color: body, flex: '0 1 auto', minWidth: '8rem' }}
      >
        {row.key}
      </span>
      <span
        style={{
          fontFamily: MONO,
          fontWeight: 500,
          color: roleColor(theme, TONE_ROLE[row.tone ?? 'default']),
          fontVariantNumeric: 'tabular-nums',
          // The value takes the remaining track (`flex: 1 1 auto` +
          // `minWidth: 0` so long values wrap instead of overflowing)
          // and stays flush-right, preserving the leader-dot rhythm.
          flex: '1 1 auto',
          minWidth: 0,
          textAlign: 'right',
        }}
      >
        {row.value}
      </span>
    </div>
  );

  /* TWO-UP LEDGER (compactness) — rows read as side-by-side pairs at the A4
     measure via the per-cell `flex-basis` floor; each half-row keeps its own
     dotted leader rule so the pair reads as two spec-sheet columns. An odd
     trailing row grows full-width.

     P-GROUP Phase A (`row`) — each PAIR is its own small flex line stamped
     `data-layout-group="row"` (atomic), instead of the former ONE flex-wrap
     container holding every cell. pagedjs cannot fragment a flex container,
     so the old single container was one unfragmentable box: a datasheet
     taller than the space remaining was pushed whole — or, when taller than a
     FULL page, CLIPPED mid-row with the tail LOST (the live SITE-SPECIFIC
     RESEARCH failure). Pair-per-line flex keeps the exact two-up geometry
     (cells share the same flex-basis floor, so the wrap points are identical)
     while the container becomes plain block flow that fragments BETWEEN rows. */
  const renderPair = (
    pair: DatasheetRow[],
    key: number,
    rowId?: string,
  ): React.ReactElement => (
    <div
      key={key}
      id={rowId}
      data-layout-group="row"
      style={{ display: 'flex', flexWrap: 'wrap', columnGap: dense ? 20 : 28 }}
    >
      {pair.map((row, i) => renderCell(row, i))}
    </div>
  );

  return (
    <div id={id} className={className} style={style}>
      {renderGroups.map((group, g) => {
        const hasHeader = group.heading != null || group.meta != null;
        const pairs = rowLayout === 'single'
          ? group.rows.map((row) => [row])
          : chunkPairs(group.rows);
        // Stable ids let the bounded post-layout overflow collector force one
        // specific row onto the next sheet when Paged.js ignores its atomic
        // break at the running-footer seam. Datasheets without an id retain
        // their existing DOM byte shape.
        const rowId = (pairIndex: number): string | undefined => (
          id ? `${id}-group-${g + 1}-row-${pairIndex + 1}` : undefined
        );
        const headerNode = hasHeader ? (
          <div
            style={{
              fontFamily: MONO,
              fontSize: REPORT_TEXT_PX.caption,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: ink,
              borderBottom: `1.5px solid ${ink}`,
              paddingBottom: 5,
              marginBottom: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
            }}
          >
            <span>{group.heading}</span>
            {group.meta != null && (
              <span style={{ color: muted, fontSize: REPORT_TEXT_PX.micro, letterSpacing: '0.06em' }}>
                {group.meta}
              </span>
            )}
          </div>
        ) : null;
        return (
          // P-GROUP Phase A (`row-group`) — the group container is explicitly
          // FRAGMENTABLE in the paged deck (`break-inside: auto` + block flow
          // via the deck's [data-layout-group="row-group"] rule), replacing the
          // former whole-group atomicity: a tall group now flows across pages
          // with each ROW pair atomic and the header bound to the first pair
          // (`head-body`), instead of clipping mid-row at the sheet edge.
          // `data-report-block="datasheet-group"` is KEPT for the Chromium
          // @media-print fallback (native print breaks an oversized avoid box
          // instead of clipping, so the group-atomic preference is safe there).
          <div
            key={g}
            data-report-block="datasheet-group"
            data-layout-group="row-group"
            style={{ marginTop: g === 0 ? 0 : 16 }}
          >
            {headerNode != null && pairs.length > 0 ? (
              // The first stable break target owns BOTH the group header and
              // its first row. A forced page break on the row alone would
              // strand the header at the donor-page foot.
              <div data-layout-group="head-body" id={rowId(0)}>
                {headerNode}
                {renderPair(pairs[0], 0)}
              </div>
            ) : (
              <>
                {headerNode}
                {pairs.length > 0 && renderPair(pairs[0], 0, rowId(0))}
              </>
            )}
            {pairs.slice(1).map((pair, i) => renderPair(pair, i + 1, rowId(i + 1)))}
          </div>
        );
      })}
    </div>
  );
}
